import { useState } from 'react';
import { useReplaceTemplateFile, type TemplateAnimationInspection } from '@/api/queries/templates';
import { checkThumbnail, precheckLottie, TemplateFileError, THUMBNAIL_TYPES } from '@/features/templates/template-files';
import { FileDropzone } from '@/features/templates/FileDropzone';
import { errorMessage } from '@/features/templates/template-ui';

/**
 * Redépôt des fichiers d'un BROUILLON. Sans lui, un dépôt interrompu ou un Lottie refusé à
 * l'analyse laissait un brouillon inutilisable, à archiver et recréer entièrement.
 * Réservé aux brouillons, comme côté serveur : l'animation d'un template publié est le
 * contrat des vidéos déjà créées par les clients.
 */
export function TemplateFilesPanel({
  templateId,
  isAnimationConfirmed,
  isThumbnailConfirmed,
  onInspected,
}: {
  templateId: string;
  isAnimationConfirmed: boolean;
  isThumbnailConfirmed: boolean;
  onInspected: (inspection: TemplateAnimationInspection) => void;
}) {
  const replaceFile = useReplaceTemplateFile();
  const [localError, setLocalError] = useState<string | undefined>(undefined);

  const onFile = async (kind: 'animation' | 'thumbnail', file: File | null) => {
    if (!file) {
      return;
    }

    setLocalError(undefined);

    try {
      // Contrôle préalable dans le navigateur : un mauvais fichier est refusé avant de
      // remplacer celui déjà en place sur le brouillon.
      if (kind === 'animation') {
        await precheckLottie(file);
      } else {
        checkThumbnail(file);
      }
    } catch (error) {
      setLocalError(error instanceof TemplateFileError ? error.message : 'Fichier illisible.');

      return;
    }

    const inspection = await replaceFile.mutateAsync({ templateId, kind, file }).catch(() => null);

    if (inspection) {
      onInspected(inspection);
    }
  };

  const error = localError ?? (replaceFile.isError ? errorMessage(replaceFile.error, 'Le dépôt a échoué.') : undefined);

  return (
    <fieldset className="flex flex-col gap-2 rounded-lg bg-ink-50 p-3" disabled={replaceFile.isPending}>
      <legend className="px-1 text-xs font-medium text-ink-500">Fichiers du brouillon</legend>

      <FileDropzone
        compact
        label={isAnimationConfirmed ? 'Remplacer l’animation (JSON)' : 'Redéposer l’animation (JSON)'}
        hint={isAnimationConfirmed ? undefined : 'Animation manquante ou refusée à l’analyse.'}
        accept="application/json,.json"
        file={null}
        disabled={replaceFile.isPending}
        onFile={(file) => void onFile('animation', file)}
      />
      <FileDropzone
        compact
        label={isThumbnailConfirmed ? 'Remplacer la vignette' : 'Redéposer la vignette'}
        hint={isThumbnailConfirmed ? undefined : 'Vignette manquante.'}
        accept={THUMBNAIL_TYPES.join(',')}
        file={null}
        disabled={replaceFile.isPending}
        onFile={(file) => void onFile('thumbnail', file)}
      />

      {replaceFile.isPending ? <p className="text-xs text-ink-500">Dépôt et analyse en cours…</p> : null}
      {error ? (
        <p role="alert" className="text-xs font-medium text-danger-500">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
